import React, { useState } from 'react';
import { LifeBuoyIcon, SendIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card, SectionHeading } from '../../components/ui/Panels';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { useAppData } from '../../contexts/AppDataContext';
import { useLocale } from '../../contexts/LocaleContext';
import { supportTopics } from '../../data/support';
import { relativeTime, dateWithYear } from '../../utils/format';
import { cn } from '../../utils/cn';
import type { TicketCategory } from '../../types/support';
const statusTone = {
  open: 'accent',
  awaiting_you: 'action',
  resolved: 'success'
} as const;
const statusLabel = {
  open: 'With support',
  awaiting_you: 'Needs your reply',
  resolved: 'Resolved'
};
export function Support() {
  const {
    tickets,
    createTicket,
    replyTicket
  } = useAppData();
  const {
    locale
  } = useLocale();
  const [activeId, setActiveId] = useState<string | null>(tickets[0]?.id ?? null);
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<TicketCategory>('booking');
  const [body, setBody] = useState('');
  const [ref, setRef] = useState('');
  const [reply, setReply] = useState('');
  const [error, setError] = useState<string | null>(null);
  const active = tickets.find(t => t.id === activeId);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (subject.trim().length < 4 || body.trim().length < 10) {
      setError('A short subject and a couple of sentences, please.');
      return;
    }
    setError(null);
    const id = createTicket({
      subject: subject.trim(),
      category,
      body: body.trim(),
      bookingRef: ref.trim() || undefined
    });
    setActiveId(id);
    setSubject('');
    setBody('');
    setRef('');
  };
  return <AppShell title="Support" subtitle="Real people, in your language, within a few hours" data-id="element-2482" data-mp="vhq02w90forb">
      <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]" data-id="element-2483" data-mp="3p9fyhnm5ds1m">
        <div className="space-y-4" data-id="element-2484" data-mp="3u66e69u1ilmi">
          <Card className="overflow-hidden" data-id="element-2485" data-mp="3u77m45fres00">
            <p className="border-b border-hairline px-4 py-3 text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-2486" data-mp="22nay26mq033">
              Your tickets
            </p>
            <ul className="divide-y divide-hairline" data-id="element-2487" data-mp="6vq80kam7ro1">
              {tickets.map(ticket => <li key={ticket.id} data-id="element-2488" data-mp="bvcjao6vuxu7">
                  <button type="button" onClick={() => setActiveId(ticket.id)} className={cn('w-full px-4 py-3 text-left transition-colors duration-150 ease-out', ticket.id === activeId ? 'bg-primary-soft' : 'hover:bg-oat')} data-id="element-2489" data-mp="gra16yxsoc93">
                    <span className="flex items-baseline justify-between gap-2" data-id="element-2490" data-mp="2rk4iava1u1ok">
                      <span className="tnum text-[11px] text-ink-3" data-id="element-2491" data-mp="2rm77xe7ahssr">{ticket.ref}</span>
                      <Badge tone={statusTone[ticket.status]} data-id="element-2492" data-mp="2k1wjz6ik6ph8">{statusLabel[ticket.status]}</Badge>
                    </span>
                    <span className="mt-1 block text-sm font-medium text-ink" data-id="element-2493" data-mp="2aas484l270lc">{ticket.subject}</span>
                    <span className="mt-0.5 block text-[12px] text-ink-3" data-id="element-2494" data-mp="2696xl0z4bijl">{relativeTime(ticket.updatedAt, locale)}</span>
                  </button>
                </li>)}
            </ul>
          </Card>

          <Card className="p-4" data-id="element-2495" data-mp="2xzuw059eq9tk">
            <div className="flex items-center gap-2" data-id="element-2496" data-mp="2y1o2nkk0su1k">
              <LifeBuoyIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-2497" data-mp="2tyj39qv8l8h5" />
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-2498" data-mp="2u0m4l71uao16">Urgent on the day?</h2>
            </div>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-2" data-id="element-2499" data-mp="1ijw3u3g6qoey">
              If a chef has not arrived or something has gone wrong mid-service, mark your ticket as
              <span className="font-medium text-ink" data-id="element-2500" data-mp="31h7kf7jucev1"> Safety</span> — those jump the queue and get a call back.
            </p>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-2501" data-mp="369iis5yxnslh">
          {active && <Card className="p-5" data-id="element-2502" data-mp="2sh2lllspqosh">
              <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-2503" data-mp="2si3xttlgthbs">
                <div data-id="element-2504" data-mp="2wj5y8dr4bzfc">
                  <h2 className="font-display text-lg font-semibold text-ink" data-id="element-2505" data-mp="2winxp4xfaz1m">{active.subject}</h2>
                  <p className="mt-0.5 text-[12px] text-ink-3" data-id="element-2506" data-mp="3k88iltd506dj">
                    {active.ref} · opened {dateWithYear(active.createdAt, locale)}
                    {active.bookingRef ? ` · ${active.bookingRef}` : ''}
                  </p>
                </div>
                <Badge tone={statusTone[active.status]} data-id="element-2507" data-mp="3g5c4ofyjkyfx">{statusLabel[active.status]}</Badge>
              </div>

              <ol className="mt-5 space-y-4" data-id="element-2508" data-mp="1zqtyrgn8fg7k">
                {active.messages.map(message => <li key={message.id} className={cn('rounded-xl px-4 py-3', message.from === 'you' ? 'bg-primary text-on-primary' : 'bg-oat text-ink')} data-id="element-2509" data-mp="24rzyhyfb83ql">
                    <p className="text-[12px] font-semibold opacity-80" data-id="element-2510" data-mp="268doklfz30ry">{message.author}</p>
                    <p className="mt-1 text-[15px] leading-relaxed" data-id="element-2511" data-mp="218iyul7xoh8h">{message.body}</p>
                    <p className="mt-1.5 text-[11px] opacity-70" data-id="element-2512" data-mp="1dj6r7ps98h8a">{relativeTime(message.at, locale)}</p>
                  </li>)}
              </ol>

              {active.status !== 'resolved' && <form className="mt-4 flex items-end gap-2 border-t border-hairline pt-4" onSubmit={e => {
            e.preventDefault();
            if (reply.trim().length < 2) return;
            replyTicket(active.id, reply.trim());
            setReply('');
          }} data-id="element-2513" data-mp="1n9ro98sa5dp3">
                  <Field label="Your reply" id="s-reply" className="flex-1" data-id="element-2514" data-mp="ps18n2uxjaua">
                    <Textarea id="s-reply" rows={2} value={reply} onChange={e => setReply(e.target.value)} data-id="element-2515" data-mp="2pvv8i2t8i84r" />
                  </Field>
                  <Button type="submit" variant="primary" size="md" disabled={reply.trim().length < 2} data-id="element-2516" data-mp="2zm6myjajnzpw">
                    <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-2517" data-mp="33o1l5ex6hwzg" />
                    <span className="sr-only" data-id="element-2518" data-mp="33pcq3i2d1as5">Send reply</span>
                  </Button>
                </form>}
            </Card>}

          <Card className="p-5" data-id="element-2519" data-mp="2bx58aeb4bsc6">
            <SectionHeading level={2} title="Open a new ticket" hint="One thing per ticket keeps answers fast." data-id="element-2520" data-mp="2bygcugce144c" />
            <form onSubmit={submit} className="mt-4 space-y-4" data-id="element-2521" data-mp="2kx0owyng0fjl">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-2522" data-mp="2kylj2n5uhwgx">
                <Field label="What is it about" id="s-cat" data-id="element-2523" data-mp="3rdm6g4gt16bw">
                  <Select id="s-cat" value={category} onChange={e => setCategory(e.target.value as TicketCategory)} data-id="element-2524" data-mp="3rf73q1ui73dg">
                    {supportTopics.map(topic => <option key={topic.id} value={topic.id} data-id="element-2525" data-mp="aa899nanhxse">
                        {topic.label}
                      </option>)}
                  </Select>
                </Field>
                <Field label="Booking reference" id="s-ref" hint="optional" data-id="element-2526" data-mp="jdm8gpkyu5w">
                  <Input id="s-ref" value={ref} onChange={e => setRef(e.target.value)} placeholder="PA-1004" data-id="element-2527" data-mp="3sno9r3151r0a" />
                </Field>
              </div>
              <Field label="Subject" id="s-subject" data-id="element-2528" data-mp="3iqjhqmkm31bk">
                <Input id="s-subject" value={subject} onChange={e => setSubject(e.target.value)} data-id="element-2529" data-mp="3ips06fnun44a" />
              </Field>
              <Field label="What happened" id="s-body" data-id="element-2530" data-mp="3dtjc2croc0kz">
                <Textarea id="s-body" rows={4} value={body} onChange={e => setBody(e.target.value)} data-id="element-2531" data-mp="39qmxcx598omb" />
              </Field>
              {error && <p role="alert" className="text-sm font-medium text-danger" data-id="element-2532" data-mp="1bxr6lbl4krh6">
                  {error}
                </p>}
              <Button type="submit" variant="action" data-id="element-2533" data-mp="1r701oduw8rau">
                Send to support
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </AppShell>;
}